import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import { PageHeader, Section, Tabs } from '../components/form';
import { Alert, Badge, Button } from '../components/ui';
import { api } from '../lib/api';

interface Endpoint {
  method: string;
  path: string;
  summary?: string;
  /** Set when the endpoint exists but this PBX has no feature behind it. */
  unavailable?: string;
}

interface Reference {
  baseUrl: string;
  prefix: string;
  total: number;
  unavailable: number;
  resources: Array<{ name: string; endpoints: Endpoint[] }>;
  errorCodes: Array<{ name: string; code: number }>;
}

/** Copy-to-clipboard that says it worked, because a silent copy is indistinguishable from a dead button. */
function CodeBlock({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-[--radius] border border-border bg-muted/40 p-3 text-xs leading-relaxed">
        <code>{children}</code>
      </pre>
      <Button
        variant="ghost"
        className="absolute right-1.5 top-1.5 h-7 px-2"
        onClick={() => {
          void navigator.clipboard.writeText(children).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </Button>
    </div>
  );
}

const METHOD_TONE: Record<string, 'success' | 'muted' | 'destructive'> = {
  GET: 'muted',
  POST: 'success',
};

/**
 * The API reference, generated from the routes the server actually serves.
 *
 * Deliberately not a hand-written document. A reference that lists an endpoint which does not
 * exist is worse than no reference at all — it sends an integrator hunting for a mistake on their
 * own side. The endpoint list here comes from Fastify's route table, so it cannot claim more than
 * the server does.
 */
export function ApiReference() {
  const [tab, setTab] = useState('start');

  const { data, isLoading } = useQuery({
    queryKey: ['api-reference'],
    queryFn: () => api.get<Reference>('/api/v1/api-reference'),
  });

  if (isLoading || !data) return null;

  const base = data.baseUrl;

  return (
    <div className="space-y-5">
      <PageHeader
        title="API"
        description={`A drop-in compatibility API. ${data.total} endpoints, so an integration written against an existing PBX can change its base URL and credentials and keep working.`}
      />

      <Tabs
        tabs={[
          { id: 'start', label: 'Getting started' },
          { id: 'endpoints', label: `Endpoints (${data.total - data.unavailable})` },
          { id: 'errors', label: 'Error codes' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'start' && (
        <div className="space-y-5">
          <Section
            title="1. Create a credential"
            description="Extensions and Trunks → API credentials. The Access Key is shown once and cannot be retrieved afterwards."
          >
            <p className="text-sm text-muted-foreground">
              The same credential works for this API and for the Web SDK. This API calls them{' '}
              <code>username</code> and <code>password</code> in the request body — they are the
              Access ID and Access Key, not a user account.
            </p>
          </Section>

          <Section title="2. Exchange it for a token" description="Valid for 30 minutes.">
            <CodeBlock>{`curl -X POST '${base}/get_token' \\
  -H 'content-type: application/json' \\
  -d '{"username":"<access id>","password":"<access key>"}'`}</CodeBlock>

            <p className="mt-3 text-sm text-muted-foreground">Responds with:</p>
            <CodeBlock>{`{
  "errcode": 0,
  "errmsg": "SUCCESS",
  "access_token": "...",
  "access_token_expire_time": 1800,
  "refresh_token": "...",
  "refresh_token_expire_time": 86400
}`}</CodeBlock>
          </Section>

          <Section
            title="3. Call an endpoint"
            description="The token goes in the query string, which is what existing integrations already send."
          >
            <CodeBlock>{`curl '${base}/extension/list?page=1&page_size=20&access_token=<token>'`}</CodeBlock>

            <Alert className="mt-4">
              A token in a query string ends up in access logs and proxy logs. This PBX redacts it
              from its own request log, but anything in front of it sees the real value — worth
              knowing when deciding how long to keep those logs. An{' '}
              <code>Authorization: Bearer &lt;token&gt;</code> header is also accepted if you are
              writing something new.
            </Alert>
          </Section>

          <Section
            title="4. Refresh before it expires"
            description="A refresh token is single-use and lasts 24 hours."
          >
            <CodeBlock>{`curl -X POST '${base}/refresh_token' \\
  -H 'content-type: application/json' \\
  -d '{"refresh_token":"<refresh token>"}'`}</CodeBlock>
            <p className="mt-3 text-sm text-muted-foreground">
              The old access token stays valid until it expires, so requests already in flight when
              you refresh do not fail.
            </p>
          </Section>

          <Section
            title="Things that differ from ordinary REST"
            description="All three are conventions of the API this is compatible with, reproduced on purpose."
          >
            <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
              <li>
                <strong className="text-foreground">Failures return HTTP 200.</strong> The outcome
                is <code>errcode</code> in the body — check that, not the status. The one exception
                is authentication, which returns 401.
              </li>
              <li>
                <strong className="text-foreground">Delete is a GET.</strong> For example{' '}
                <code>GET /extension/delete?id=3</code>. It accepts several ids at once as{' '}
                <code>id=1,2,3</code>.
              </li>
              <li>
                <strong className="text-foreground">Objects are identified by integers</strong>, not
                the UUIDs the admin API uses. They are stable, and they can be imported, so ids you
                already store against a an existing PBX can be preserved when you migrate.
              </li>
            </ul>
          </Section>

          <Section
            title="What this PBX does not implement"
            description="Documented endpoints with no feature behind them."
          >
            <p className="text-sm text-muted-foreground">
              SMS, WhatsApp and live-chat messaging, hotel integration, onsite proxy and uACSTA are
              not implemented. They answer <code>70656 API FUNCTION IS NOT ENABLED</code> — a
              defined reference-API code, rather than a 404 that would read as a broken deployment.
              Anything else not listed under Endpoints answers{' '}
              <code>10001 INTERFACE NOT EXISTED</code>.
            </p>
          </Section>
        </div>
      )}

      {tab === 'endpoints' && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Generated from the server's own route table, so this list is exactly what is served. All
            paths are relative to <code>{base}</code>.
            {data.unavailable > 0 ? (
              <>
                {' '}
                {data.unavailable} of them are documented upstream but have no feature behind them
                on this PBX; they are marked below and answer <code>70656</code> rather than
                pretending to work.
              </>
            ) : null}
          </p>

          {data.resources.map((resource) => (
            <Section key={resource.name} title={resource.name}>
              <div className="divide-y divide-border/60">
                {resource.endpoints.map((endpoint) => (
                  <div
                    key={`${endpoint.method} ${endpoint.path}`}
                    className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2 first:pt-0 last:pb-0"
                  >
                    <Badge
                      tone={
                        endpoint.unavailable ? 'muted' : (METHOD_TONE[endpoint.method] ?? 'muted')
                      }
                    >
                      {endpoint.method}
                    </Badge>
                    <code
                      className={endpoint.unavailable ? 'text-xs text-muted-foreground' : 'text-xs'}
                    >
                      {endpoint.path.replace(data.prefix, '')}
                    </code>

                    {endpoint.unavailable ? (
                      <span className="text-xs text-muted-foreground">
                        <Badge tone="warning">not available</Badge> {endpoint.unavailable} — answers{' '}
                        <code>70656</code>
                      </span>
                    ) : endpoint.summary ? (
                      <span className="text-xs text-muted-foreground">{endpoint.summary}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            </Section>
          ))}
        </div>
      )}

      {tab === 'errors' && (
        <Section
          title="Error codes"
          description="The original numbering, reproduced exactly — a client that retries on 10004 and gives up on 60001 keeps working."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-2 pr-4 font-medium">Code</th>
                  <th className="py-2 font-medium">Meaning</th>
                </tr>
              </thead>
              <tbody>
                {data.errorCodes.map((entry) => (
                  <tr key={entry.code} className="border-b border-border/50">
                    <td className="py-1.5 pr-4 font-mono tabular-nums">{entry.code}</td>
                    <td className="py-1.5 text-muted-foreground">
                      {entry.name.replace(/_/g, ' ').toLowerCase()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}
